import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { analizarItem, leerEntero, leerVence, moldeItem, porBlister, POR_BLISTER_SUGERIDO, unidadesItem, unidadesLote, type AnalisisItem, type ItemIngreso, type LoteIngreso } from "@huayruro/shared";
import { mutar, useApi } from "../lib/useApi";
import { ahoraFecha } from "../lib/fecha-ui";
import { useLectorRafaga } from "../lib/useLectorRafaga";
import { Button, Chip, EmptyState, Input, cn, useToast } from "../components/ui";

// «Ingreso de inventario» (brief 26-oct-2026, paso 1): la captura de captura.html, ahora en el sistema y
// con el borrador en el SERVIDOR. Tramo 10 (27-oct): cada producto con unidad, blíster y caja (cada uno
// con su precio) y sus lotes. Tramo 11 (Kevin, 29-oct: «me refiero a lo visual, quiero dejar de usar el
// html»): se ve como captura.html — la barra oscura dice el siguiente paso, un producto a la vez en una
// ficha grande con lo que falta en rojo, «Lo capturado» abajo con filtros, y abajo los contadores con
// «Revisar y subir». La repisa es un campo de arriba: se abre sola la última a medias.

type Item = ItemIngreso & { carga_id: string | null; desenlace: string | null; analisis: AnalisisItem | null };
type Repisa = { id: string; repisa: string; estado: "abierta" | "subida" | "descartada"; usuario_nombre: string | null; pendientes: number; subidos: number; updated_at: string };

const conSuc = (path: string, suc?: string) => (suc ? `${path}${path.includes("?") ? "&" : "?"}sucursal_id=${suc}` : path);
// El vencimiento como lo trae la caja (mes/año): «30/09» sin año confundía 2027 con 2028 (captura del 29-oct).
const mesAnio = (iso: string) => `${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const horaCorta = (iso: string) => new Date(iso).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit", hour12: false });

// La última repisa abierta, por botica: al volver al Ingreso se sigue en ella sin pasar por una lista.
const claveRepisa = (suc?: string) => `huayruro:ingreso-repisa:${suc ?? "propia"}`;
function leerLocal(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function guardarLocal(k: string, v: string | null) {
  try {
    if (v) localStorage.setItem(k, v);
    else localStorage.removeItem(k);
  } catch {
    /* sin almacenamiento: se elige la repisa a mano */
  }
}

// Pitido corto del escaneo (como captura.html): bien = agudo, mal = grave. Sin sonido no pasa nada.
let audio: AudioContext | null = null;
function sonar(ok: boolean) {
  try {
    audio ??= new AudioContext();
    const o = audio.createOscillator();
    const g = audio.createGain();
    o.frequency.value = ok ? 880 : 220;
    g.gain.value = 0.06;
    o.connect(g);
    g.connect(audio.destination);
    o.start();
    o.stop(audio.currentTime + (ok ? 0.07 : 0.25));
  } catch {
    /* sin sonido */
  }
}

export function IngresoInventario({ sucursalId }: { sucursalId?: string | undefined }) {
  const { data, cargando, error, recargar } = useApi<{ repisas: Repisa[] }>(conSuc("/ingreso/repisas", sucursalId), [sucursalId]);
  const [repisaId, setRepisaId] = useState<string | null>(null);
  const [eligiendo, setEligiendo] = useState(false);
  const clave = claveRepisa(sucursalId);

  // Sin elegir nada: la repisa a medias que se usó la última vez en esta botica, o la más reciente. Se
  // calcula al pintar (no en un efecto): así no parpadea «Escribe en qué repisa estás» antes de abrirla.
  const auto = useMemo(() => {
    if (repisaId || eligiendo || !data) return null;
    const abiertas = data.repisas.filter((r) => r.estado === "abierta");
    const guardada = leerLocal(clave);
    return (abiertas.find((x) => x.id === guardada) ?? abiertas[0])?.id ?? null;
  }, [data, repisaId, eligiendo, clave]);
  const abiertaId = repisaId ?? auto;

  const abrir = (id: string) => {
    guardarLocal(clave, id);
    setRepisaId(id);
    setEligiendo(false);
  };
  const cambiar = () => {
    if (!repisaId && abiertaId) setRepisaId(abiertaId);
    setEligiendo(true);
    recargar();
  };

  if (abiertaId && !eligiendo) {
    return (
      <RepisaAbierta
        key={abiertaId}
        id={abiertaId}
        sucursalId={sucursalId}
        onCambiar={cambiar}
        onDescartada={() => {
          guardarLocal(clave, null);
          setRepisaId(null);
          cambiar();
        }}
      />
    );
  }
  if (cargando && !data) return <p className="p-6 text-center text-[13px] text-ink-3">Cargando repisas…</p>;
  if (error) {
    return (
      <p className="p-4 text-center text-[13px] text-accent-ink">
        {error}{" "}
        <button className="underline" onClick={recargar}>
          Reintentar
        </button>
      </p>
    );
  }
  const actual = data?.repisas.find((r) => r.id === repisaId) ?? null;
  return <ElegirRepisa repisas={data?.repisas ?? []} sucursalId={sucursalId} onAbrir={abrir} volverA={actual} onVolver={() => setEligiendo(false)} />;
}

// ---------------- Elegir o empezar una repisa ----------------
function ElegirRepisa({ repisas, sucursalId, onAbrir, volverA, onVolver }: { repisas: Repisa[]; sucursalId?: string | undefined; onAbrir: (id: string) => void; volverA: Repisa | null; onVolver: () => void }) {
  const toast = useToast();
  const [nombre, setNombre] = useState("");
  const [creando, setCreando] = useState(false);
  const abiertas = repisas.filter((r) => r.estado === "abierta");
  const cerradas = repisas.filter((r) => r.estado !== "abierta");

  async function crear() {
    if (!nombre.trim()) return;
    setCreando(true);
    try {
      const r = await mutar<{ id: string }>(conSuc("/ingreso/repisas", sucursalId), { method: "POST", body: { repisa: nombre.trim() } });
      setNombre("");
      onAbrir(r.id);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setCreando(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <BarraGuia chip="1" texto="Escribe en qué repisa estás" detalle="(así queda guardado dónde está cada producto)" />
      <div className="rounded-[12px] border border-line bg-card p-4 sm:p-5">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void crear();
          }}
        >
          <label htmlFor="nombre-repisa" className="text-[14px] font-bold text-ink">
            Repisa
          </label>
          <Input
            id="nombre-repisa"
            aria-label="Nombre de la repisa"
            placeholder="Ej. Repisa A · Antibióticos"
            value={nombre}
            maxLength={60}
            autoFocus
            onChange={(e) => setNombre(e.target.value)}
            className="min-h-[48px] min-w-0 flex-1 basis-[220px] text-[17px]"
          />
          <Button type="submit" disabled={creando || !nombre.trim()} className="min-h-[48px]">
            Empezar repisa
          </Button>
          {volverA && (
            <Button variant="outline" onClick={onVolver} className="min-h-[48px]">
              Seguir en {volverA.repisa}
            </Button>
          )}
        </form>
        <p className="mt-2 text-[12.5px] text-ink-2">Todo se guarda solo en el sistema: si cambias de equipo, sigue aquí.</p>
      </div>

      {abiertas.length > 0 && (
        <section aria-label="Repisas a medias" className="flex flex-col gap-2 rounded-[12px] border border-line bg-card p-4">
          <h3 className="text-[14px] font-bold text-ink">A medias</h3>
          {abiertas.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onAbrir(r.id)}
              className="flex min-h-[52px] items-center gap-3 rounded-[10px] border border-line-input px-3 py-2 text-left hover:bg-hover-btn"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14.5px] font-semibold text-ink">{r.repisa}</span>
                <span className="block text-[12px] text-ink-2">
                  {r.usuario_nombre ?? "—"} · {r.pendientes} por subir{r.subidos ? ` · ${r.subidos} ya subidos` : ""} · {horaCorta(r.updated_at)}
                </span>
              </span>
              <span className="text-[13px] font-semibold text-link">Seguir →</span>
            </button>
          ))}
        </section>
      )}
      {abiertas.length === 0 && cerradas.length === 0 && <EmptyState title="Todavía no cargaste ninguna repisa" subtitle="Escribe el nombre de la repisa y empieza a escanear." />}
      {cerradas.length > 0 && (
        <details className="rounded-[12px] border border-line bg-card px-4 py-2">
          <summary className="min-h-[40px] cursor-pointer py-2 text-[13px] font-semibold text-ink-emph">Subidas en los últimos 14 días ({cerradas.length})</summary>
          <ul className="pb-2">
            {cerradas.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 border-t border-line-row py-2 text-[13px]">
                <button type="button" className="min-w-0 truncate text-left text-ink hover:underline" onClick={() => onAbrir(r.id)}>
                  {r.repisa}
                </button>
                <span className="flex-none text-[12px] text-ink-2">
                  {r.estado === "subida" ? `${r.subidos} subidos` : "descartada"} · {r.usuario_nombre ?? "—"}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** La barra oscura de captura.html: un solo paso, el que toca ahora. */
function BarraGuia({ chip, texto, detalle, tono = "normal" }: { chip: string; texto: string; detalle?: string | undefined; tono?: "normal" | "listo" | undefined }) {
  return (
    <div data-testid="guia" role="status" className="sticky top-0 z-10 flex items-center gap-3 rounded-[12px] bg-ink-surface px-4 py-3 text-on-dark shadow-[0_2px_8px_rgba(36,29,26,0.18)]">
      <span className={cn("flex-none rounded-full px-2.5 py-0.5 font-mono text-[12.5px] font-bold text-white", tono === "listo" ? "bg-ok-strong" : "bg-accent")}>{chip}</span>
      <span className="min-w-0 text-[16px] font-bold leading-snug sm:text-[17px]">
        {texto}
        {detalle && <span className="ml-2 text-[13px] font-normal text-on-dark-2">{detalle}</span>}
      </span>
    </div>
  );
}

// ---------------- Una repisa ----------------
type Vista = { repisa: Repisa; items: Item[] };
type Guardado = { estado: "listo" | "guardando" | "error"; hora: string | null; error?: string };
type CamposRenglon = Partial<Omit<ItemIngreso, "lotes">>;
type CamposLote = Partial<Omit<LoteIngreso, "id">>;
type Escaneo = { accion: "nuevo" | "sumado" | "lote_nuevo"; item_id: string; lote_id: string };
type Filtro = "todos" | "problemas" | "listos";

/** Suelta de lo pendiente lo que ya llegó al servidor y no volvió a cambiar mientras viajaba. */
function soltar<C extends Record<string, unknown>>(set: (f: (c: Record<string, C>) => Record<string, C>) => void, id: string, envio: C) {
  set((c) => {
    const ahora = c[id] ?? ({} as C);
    const resto = Object.fromEntries(Object.entries(ahora).filter(([k, v]) => envio[k] !== v)) as C;
    const n = { ...c };
    if (Object.keys(resto).length) n[id] = resto;
    else delete n[id];
    return n;
  });
}

function RepisaAbierta({ id, sucursalId, onCambiar, onDescartada }: { id: string; sucursalId?: string | undefined; onCambiar: () => void; onDescartada: () => void }) {
  const toast = useToast();
  const { data, error, recargar } = useApi<Vista>(conSuc(`/ingreso/repisas/${id}`, sucursalId), [id, sucursalId]);
  // Lo tecleado y aún no confirmado por el servidor: se pinta encima de lo que vino del servidor. El
  // renglón y cada lote se guardan aparte, pero con el MISMO reloj por renglón.
  const [cambios, setCambios] = useState<Record<string, CamposRenglon>>({});
  const [cambiosLote, setCambiosLote] = useState<Record<string, CamposLote>>({});
  const [guardado, setGuardado] = useState<Guardado>({ estado: "listo", hora: null });
  const [abiertoId, setAbiertoId] = useState<string | null>(null);
  // A qué producto llevarle el cursor (al que llegó por el lector o «sin código»), y una vez por llegada.
  const [cursor, setCursor] = useState<{ id: string; n: number } | null>(null);
  const [aviso, setAviso] = useState<{ texto: string; tipo: "ok" | "mal" | "info" } | null>(null);
  const [codigo, setCodigo] = useState("");
  const [subir, setSubir] = useState(false);
  const timers = useRef<Record<string, number>>({});
  const duenoLote = useRef<Record<string, string>>({}); // lote → su renglón
  const abierta = data?.repisa.estado === "abierta";
  const hoy = ahoraFecha();

  const items = useMemo<Item[]>(
    () => (data?.items ?? []).map((i) => ({ ...i, ...(cambios[i.id] ?? {}), lotes: i.lotes.map((l) => ({ ...l, ...(cambiosLote[l.id] ?? {}) })) })),
    [data, cambios, cambiosLote],
  );
  const pendientes = items.filter((i) => !i.carga_id);
  const analisis = useMemo(() => new Map(pendientes.map((i) => [i.id, analizarItem(i, pendientes, hoy)])), [pendientes, hoy]);
  const listos = pendientes.filter((i) => analisis.get(i.id)!.problemas.length === 0).length;
  const conProblemas = pendientes.length - listos;

  // El producto de la ficha: el que se escaneó o se tocó; si no, el último a medias de la repisa.
  const abierto = items.find((i) => i.id === abiertoId) ?? (abierta ? (pendientes.at(-1) ?? null) : null);

  // Lo tecleado se manda medio segundo después de dejar de escribir en ese renglón (o en sus lotes).
  const cambiosRef = useRef(cambios);
  cambiosRef.current = cambios;
  const cambiosLoteRef = useRef(cambiosLote);
  cambiosLoteRef.current = cambiosLote;
  const enviar = useCallback(
    (itemId: string): Promise<void> => {
      window.clearTimeout(timers.current[itemId]);
      const envio = cambiosRef.current[itemId];
      const lotes = Object.entries(cambiosLoteRef.current).filter(([lid, c]) => duenoLote.current[lid] === itemId && Object.keys(c).length > 0);
      if (!envio && lotes.length === 0) return Promise.resolve();
      setGuardado({ estado: "guardando", hora: null });
      const pedidos: Promise<unknown>[] = [];
      if (envio) pedidos.push(mutar(conSuc(`/ingreso/items/${itemId}`, sucursalId), { method: "PATCH", body: envio }).then(() => soltar(setCambios, itemId, envio)));
      for (const [lid, c] of lotes) pedidos.push(mutar(conSuc(`/ingreso/lotes/${lid}`, sucursalId), { method: "PATCH", body: c }).then(() => soltar(setCambiosLote, lid, c)));
      return Promise.all(pedidos)
        .then(() => {
          setGuardado({ estado: "listo", hora: new Date().toISOString() });
          recargar();
        })
        .catch((e: unknown) => setGuardado({ estado: "error", hora: null, error: e instanceof Error ? e.message : String(e) }));
    },
    [sucursalId, recargar],
  );
  const programar = useCallback(
    (itemId: string) => {
      window.clearTimeout(timers.current[itemId]);
      timers.current[itemId] = window.setTimeout(() => void enviar(itemId), 500);
    },
    [enviar],
  );
  const guardar = useCallback(
    (itemId: string, campos: CamposRenglon) => {
      setCambios((c) => ({ ...c, [itemId]: { ...(c[itemId] ?? {}), ...campos } }));
      cambiosRef.current = { ...cambiosRef.current, [itemId]: { ...(cambiosRef.current[itemId] ?? {}), ...campos } };
      programar(itemId);
    },
    [programar],
  );
  const guardarLote = useCallback(
    (itemId: string, loteId: string, campos: CamposLote) => {
      duenoLote.current[loteId] = itemId;
      setCambiosLote((c) => ({ ...c, [loteId]: { ...(c[loteId] ?? {}), ...campos } }));
      cambiosLoteRef.current = { ...cambiosLoteRef.current, [loteId]: { ...(cambiosLoteRef.current[loteId] ?? {}), ...campos } };
      programar(itemId);
    },
    [programar],
  );
  /** Todo lo tecleado y aún sin guardar, de todos los renglones. */
  const enviarTodo = useCallback(() => {
    const ids = new Set([...Object.keys(cambiosRef.current), ...Object.keys(cambiosLoteRef.current).map((lid) => duenoLote.current[lid]!).filter(Boolean)]);
    return Promise.all([...ids].map((itemId) => enviar(itemId)));
  }, [enviar]);

  const abrirProducto = useCallback((itemId: string, conCursor: boolean) => {
    setAbiertoId(itemId);
    setCursor((c) => (conCursor ? { id: itemId, n: (c?.n ?? 0) + 1 } : null));
  }, []);

  const escanear = useCallback(
    async (crudo: string) => {
      if (!abierta) return;
      try {
        // Primero lo tecleado y aún sin guardar: si no, ese guardado llegaría DESPUÉS del escaneo y le
        // pisaría la caja que el lector acaba de sumar (lo atrapó el e2e del 26-oct: quedaba 2 y no 3).
        await enviarTodo();
        const r = await mutar<Escaneo>(conSuc(`/ingreso/repisas/${id}/escanear`, sucursalId), { method: "POST", body: { codigo: crudo } });
        sonar(true);
        // Un producto nuevo lleva el cursor a lo que falta; sumar una caja no le quita el cursor a nadie.
        abrirProducto(r.item_id, r.accion !== "sumado");
        setAviso({
          texto: r.accion === "sumado" ? "+1 caja" : r.accion === "lote_nuevo" ? "Otro lote del mismo producto: revisa su vencimiento" : "Nuevo producto: completa lo que falta",
          tipo: "ok",
        });
        recargar();
      } catch (e) {
        sonar(false);
        setAviso({ texto: e instanceof Error ? e.message : String(e), tipo: "mal" });
      }
    },
    [abierta, id, sucursalId, recargar, enviarTodo, abrirProducto],
  );
  useLectorRafaga((c) => void escanear(c), abierta && !subir);

  async function agregarSinCodigo() {
    try {
      await enviarTodo();
      const r = await mutar<{ item_id: string }>(conSuc(`/ingreso/repisas/${id}/items`, sucursalId), { method: "POST", body: {} });
      abrirProducto(r.item_id, true);
      setAviso({ texto: "Producto sin código: escribe su nombre", tipo: "info" });
      recargar();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  async function agregarLote(itemId: string) {
    try {
      await enviar(itemId);
      await mutar(conSuc(`/ingreso/items/${itemId}/lotes`, sucursalId), { method: "POST", body: {} });
      setAviso({ texto: "Otro lote del mismo producto: escribe su número, su vencimiento y cuánto contaste", tipo: "info" });
      recargar();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  async function quitarLote(itemId: string, loteId: string) {
    try {
      await enviar(itemId);
      await mutar(conSuc(`/ingreso/lotes/${loteId}`, sucursalId), { method: "DELETE" });
      setCambiosLote((c) => {
        const n = { ...c };
        delete n[loteId];
        return n;
      });
      recargar();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  async function quitar(itemId: string) {
    if (!window.confirm("¿Quitar este producto de la repisa?")) return;
    try {
      await mutar(conSuc(`/ingreso/items/${itemId}`, sucursalId), { method: "DELETE" });
      setAbiertoId(null);
      recargar();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  async function descartar() {
    if (!window.confirm("¿Descartar esta repisa? Se borra lo que tiene sin subir.")) return;
    try {
      await mutar(conSuc(`/ingreso/repisas/${id}/descartar`, sucursalId), { method: "POST" });
      onDescartada();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }

  if (error) return <p className="p-6 text-center text-[13px] text-accent-ink">{error}</p>;
  if (!data) return <p className="p-6 text-center text-[13px] text-ink-3">Cargando repisa…</p>;
  const aAbierto = abierto ? (analisis.get(abierto.id) ?? null) : null;
  const acciones = (i: Item): AccionesRenglon => ({
    onCambio: (c) => guardar(i.id, c),
    onCambioLote: (loteId, c) => guardarLote(i.id, loteId, c),
    onAgregarLote: () => void agregarLote(i.id),
    onQuitarLote: (loteId) => void quitarLote(i.id, loteId),
    onQuitar: () => void quitar(i.id),
  });

  // Lo que dice la barra oscura: el siguiente paso del producto abierto, como en captura.html.
  const guia = !abierta
    ? { chip: "✓", texto: "Repisa subida", detalle: "para seguir, usa «Cambiar» y empieza otra", tono: "listo" as const }
    : !abierto
      ? { chip: "1", texto: "Escanea una caja con el lector", detalle: "(o escribe el código y pulsa Enter)" }
      : abierto.carga_id
        ? { chip: abierto.gtin ?? "✓", texto: "Ya subido. Escanea la siguiente caja", tono: "listo" as const }
        : aAbierto?.siguiente
          ? { chip: abierto.gtin ?? "sin código", texto: aAbierto.siguiente.texto }
          : aAbierto && aAbierto.problemas.length === 0
            ? { chip: abierto.gtin ?? "sin código", texto: "Listo. Escanea la siguiente caja", tono: "listo" as const }
            : { chip: abierto.gtin ?? "sin código", texto: aAbierto?.problemas[0] ?? "Completa lo que falta" };

  return (
    <div className="flex flex-col gap-3">
      {/* Cabecera: la repisa como un campo, y si se guardó. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[12px] border border-line bg-card px-4 py-2.5">
        <span className="text-[13px] text-ink-2">Repisa</span>
        <h2 className="min-w-0 truncate text-[17px] font-bold text-ink">{data.repisa.repisa}</h2>
        <button type="button" onClick={onCambiar} className="min-h-[40px] rounded-[9px] border border-line-input px-3 text-[13px] font-semibold text-ink-emph hover:bg-hover-btn" aria-label="Cambiar repisa">
          Cambiar
        </button>
        <span className="ml-auto text-[12px] text-ink-2" aria-live="polite">
          {guardado.estado === "guardando" ? "Guardando…" : guardado.estado === "error" ? <span className="text-accent-ink">No se guardó: {guardado.error}</span> : guardado.hora ? `Guardado ${horaCorta(guardado.hora)}` : "Se guarda solo"}
        </span>
      </div>

      <BarraGuia chip={guia.chip} texto={guia.texto} detalle={guia.detalle} tono={guia.tono} />

      {/* Código: siempre listo para el lector o para teclear. */}
      {abierta ? (
        <div className="rounded-[12px] border border-line bg-card p-4">
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (codigo.trim()) void escanear(codigo.trim());
              setCodigo("");
            }}
          >
            <label htmlFor="codigo-ingreso" className="text-[14px] font-bold text-ink">
              Código
            </label>
            <input
              id="codigo-ingreso"
              aria-label="Código de barras"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Escanea la caja o escribe el código y pulsa Enter"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              className="min-h-[52px] min-w-0 flex-1 basis-[240px] rounded-[10px] border border-line-input bg-field px-4 text-[18px] text-ink outline-none placeholder:text-ink-3-alt focus:border-ink-emph focus:ring-2 focus:ring-ink-emph/30"
            />
            <Button type="submit" variant="outline" className="min-h-[48px]">
              Agregar
            </Button>
            <Button variant="outline" onClick={() => void agregarSinCodigo()} className="min-h-[48px]">
              Producto sin código
            </Button>
          </form>
          {aviso && (
            <p role="status" className={cn("mt-2 text-[13.5px] font-semibold", aviso.tipo === "mal" ? "text-accent-ink" : aviso.tipo === "ok" ? "text-ok" : "text-info-ink")}>
              {aviso.texto}
            </p>
          )}
        </div>
      ) : (
        <p className="rounded-[12px] bg-ok-soft px-4 py-3 text-[14px] font-semibold text-ok">Esta repisa ya se subió entera.</p>
      )}

      {/* El producto abierto, en grande. */}
      {abierta &&
        (abierto ? (
          <FichaProducto key={abierto.id} item={abierto} a={aAbierto} enfocar={cursor?.id === abierto.id ? cursor.n : 0} {...acciones(abierto)} />
        ) : (
          <div className="rounded-[12px] border border-line bg-card px-4 py-8 text-center text-[14px] text-ink-2">Todavía no hay un producto abierto. Escanea una caja para empezar.</div>
        ))}

      <LoCapturado items={items} analisis={analisis} abiertoId={abierto?.id ?? null} onAbrir={(i) => abrirProducto(i, false)} />

      {abierta && (
        <button type="button" onClick={() => void descartar()} className="min-h-[40px] self-start px-1 text-[12.5px] text-ink-3 underline hover:text-accent-ink">
          Descartar repisa
        </button>
      )}

      {/* Abajo, como en captura.html: cuánto hay y «Revisar y subir». Lo incompleto se queda en la repisa. */}
      {abierta && pendientes.length > 0 && (
        <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-2 rounded-t-[12px] border-t border-line-chrome bg-card/95 px-4 py-3 backdrop-blur">
          <p className="text-[14px] text-ink">
            <strong>{pendientes.length}</strong> {pendientes.length === 1 ? "producto" : "productos"} · <strong>{listos}</strong> {listos === 1 ? "listo" : "listos"} ·{" "}
            <strong className={conProblemas ? "text-accent-ink" : ""}>{conProblemas}</strong> por arreglar
          </p>
          <Button onClick={() => setSubir(true)} disabled={listos === 0} className="min-h-[48px] justify-center text-[15px] max-sm:w-full">
            Revisar y subir · {listos} {listos === 1 ? "listo" : "listos"}
            {conProblemas > 0 ? ` (${conProblemas} quedan)` : ""}
          </Button>
        </div>
      )}

      {subir && (
        <SubirRepisa
          repisaId={id}
          sucursalId={sucursalId}
          pendientes={Object.keys(cambios).length + Object.keys(cambiosLote).length}
          onCerrar={() => {
            setSubir(false);
            recargar();
          }}
        />
      )}
    </div>
  );
}

type AccionesRenglon = {
  onCambio: (c: CamposRenglon) => void;
  onCambioLote: (loteId: string, c: CamposLote) => void;
  onAgregarLote: () => void;
  onQuitarLote: (loteId: string) => void;
  onQuitar: () => void;
};

/** «No» en suelto o en blísteres borra las sueltas o los blísteres que se habían contado en sus lotes. */
function alCambiarSiNo(item: Item, acc: AccionesRenglon, campo: "suelto" | "blister", v: boolean) {
  acc.onCambio({ [campo]: v });
  if (v) return;
  const deLote = campo === "suelto" ? "sueltas" : "blisteres";
  for (const l of item.lotes) if (l[deLote]) acc.onCambioLote(l.id, { [deLote]: "" });
}

/** El nombre accesible de un campo de lote: el primero como siempre («cajas de X»), los demás con su número. */
const etiquetaLote = (campo: string, i: number, nombre: string) => (i === 0 ? `${campo} de ${nombre}` : `${campo} del lote ${i + 1} de ${nombre}`);

// ---------------- Estado de un renglón ----------------
function EstadoRenglon({ item, a, corto }: { item: Item; a: AnalisisItem | null; corto?: boolean }) {
  if (item.carga_id) {
    return <Chip variant={item.desenlace === "omitido_existente" ? "neutral" : "ok"}>{item.desenlace === "omitido_existente" ? "Ya estaba" : "Subido"}</Chip>;
  }
  if (!a) return null;
  if (a.problemas.length === 0) return <Chip variant="ok">Listo</Chip>;
  const variante = a.problemas.some((p) => /vencido/.test(p)) ? "danger" : "warn";
  return <Chip variant={variante}>{corto ? `${a.problemas.length} por arreglar` : (a.siguiente?.texto ?? a.problemas[0])}</Chip>;
}

/**
 * «¿Viene en blísteres?» y cuántas unidades trae cada uno. Con Sí, el blíster vale lo de siempre (10) y el
 * número solo se abre para cambiarlo cuando hace falta (aciclovir 5, levofloxacino 7) o cuando está mal.
 */
function Blisteres({ item, marcado, marcadoPb, onCambio, onSiNo }: { item: Item; marcado: boolean; marcadoPb: boolean; onCambio: (c: CamposRenglon) => void; onSiNo: (v: boolean) => void }) {
  const [abierto, setAbierto] = useState(false);
  const caja = leerEntero(item.factor);
  const propio = item.por_blister.trim() !== "" && item.por_blister.trim() !== String(POR_BLISTER_SUGERIDO);
  const pb = porBlister(item);
  const nombre = item.nombre || "producto";
  return (
    <span className="flex flex-col gap-1">
      <SiNo valor={item.blister} disabled={caja === 1 && item.blister !== true} marcado={marcado} etiqueta="¿Viene en blísteres?" onCambio={onSiNo} />
      {item.blister === true &&
        (abierto || propio || marcadoPb ? (
          <label className="flex items-center gap-1.5 text-[13px] text-ink-2">
            <span>de</span>
            <input
              aria-label={`unidades por blíster de ${nombre}`}
              data-campo="por_blister"
              value={item.por_blister}
              inputMode="numeric"
              placeholder={String(POR_BLISTER_SUGERIDO)}
              autoFocus={abierto && !propio}
              onChange={(e) => onCambio({ por_blister: e.target.value })}
              className={cn(campoCss, "w-[84px]", marcadoPb && campoMal)}
            />
            <span>und por blíster</span>
          </label>
        ) : (
          <span className="text-[13px] text-ink-2">
            de {pb ?? POR_BLISTER_SUGERIDO} und{" "}
            <button type="button" onClick={() => setAbierto(true)} className="min-h-[32px] px-1 font-semibold text-link underline" aria-label={`Cambiar las unidades por blíster de ${nombre}`}>
              cambiar
            </button>
          </span>
        ))}
    </span>
  );
}

/** Sí/No grande. En rojo y verde solo lo que es alerta (¿Pide receta?); lo demás, elegido = oscuro. */
function SiNo({ valor, onCambio, disabled, marcado, etiqueta, alerta }: { valor: boolean | null; onCambio: (v: boolean) => void; disabled?: boolean; marcado?: boolean; etiqueta: string; alerta?: boolean }) {
  const b = (v: boolean, texto: string) => (
    <button
      type="button"
      aria-pressed={valor === v}
      disabled={disabled}
      onClick={() => onCambio(v)}
      className={cn(
        "min-h-[48px] flex-1 rounded-[9px] border text-[15px] font-semibold transition-colors disabled:opacity-50",
        valor !== v
          ? "border-line-input bg-card text-ink-emph hover:bg-hover-btn"
          : !alerta
            ? "border-ink-surface-2 bg-ink-surface-2 text-on-dark"
            : v
              ? "border-accent bg-accent-soft text-accent-ink"
              : "border-ok bg-ok-soft text-ok",
      )}
    >
      {texto}
    </button>
  );
  return (
    <span role="group" aria-label={etiqueta} data-campo={etiqueta} className={cn("flex gap-1.5 rounded-[10px]", marcado && "ring-2 ring-accent ring-offset-2")}>
      {b(true, "Sí")}
      {b(false, "No")}
    </span>
  );
}

// ---------------- La ficha: un producto a la vez, en grande (como captura.html) ----------------
// Sin ancho: `cn` solo junta clases (no resuelve choques), así que cada campo dice el suyo.
const campoCss = "min-h-[48px] rounded-[9px] border border-line-input bg-field px-3 text-[16px] text-ink outline-none focus:border-ink-emph focus:ring-2 focus:ring-ink-emph/25 disabled:border-transparent disabled:bg-inset disabled:text-ink-3";
const campoMal = "border-accent bg-accent-soft";
type CampoTexto = "nombre" | "factor" | "precio_unidad" | "precio_blister" | "precio_caja";
type CampoLote = "lote" | "vence" | "cajas" | "blisteres" | "sueltas";
// El campo del «siguiente paso» que corresponde a cada grupo Sí/No (para llevarle el cursor).
const GRUPO_DE: Partial<Record<string, string>> = { suelto: "¿Se vende suelto?", blister: "¿Viene en blísteres?", requiere_receta: "¿Pide receta?" };

function FichaProducto({ item, a, enfocar, ...acc }: { item: Item; a: AnalisisItem | null; enfocar: number } & AccionesRenglon) {
  const ref = useRef<HTMLDivElement>(null);
  const sig = a?.siguiente ?? null;
  const falta = (c: string, loteId?: string) => sig?.campo === c && (sig.loteId ?? null) === (loteId ?? null);
  const caja = leerEntero(item.factor);
  const porUnidad = moldeItem(item)?.cuentaPor === "unidad";
  const nombre = item.nombre || "producto";
  const total = unidadesItem(item);

  // Al abrir un producto con el lector o «sin código», el cursor va a lo que falta (como captura.html).
  useEffect(() => {
    if (!enfocar || !ref.current) return;
    const s = sig;
    if (!s) return;
    const sel = GRUPO_DE[s.campo] ? `[data-campo="${GRUPO_DE[s.campo]}"] button` : s.loteId ? `[data-lote="${s.loteId}"][data-campo="${s.campo}"]` : `[data-campo="${s.campo}"]`;
    ref.current.querySelector<HTMLElement>(sel)?.focus();
    ref.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    // Solo cuando cambia el producto abierto, no con cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enfocar]);

  const etiqueta = (c: string, texto: string, loteId?: string) => <span className={cn("text-[13px] font-semibold", falta(c, loteId) ? "text-accent-ink" : "text-ink-2")}>{texto}</span>;
  const campo = (c: Exclude<CampoTexto, "nombre">, texto: string, opc: { modo?: "numeric" | "decimal"; disabled?: boolean; placeholder?: string; nota?: string } = {}) => (
    <label className="flex flex-col gap-1">
      {etiqueta(c, texto)}
      <input
        aria-label={`${c.replace("_", " ")} de ${nombre}`}
        data-campo={c}
        value={String(item[c] ?? "")}
        disabled={opc.disabled}
        placeholder={opc.disabled ? "no aplica" : opc.placeholder}
        inputMode={opc.modo ?? "decimal"}
        autoComplete="off"
        onChange={(e) => acc.onCambio({ [c]: e.target.value })}
        className={cn(campoCss, "w-full", falta(c) && campoMal)}
      />
      {opc.nota && <span className="text-[12px] text-ink-2">{opc.nota}</span>}
    </label>
  );

  return (
    <div id={`renglon-${item.id}`} ref={ref} data-testid="ficha-producto" className="flex flex-col gap-4 rounded-[12px] border border-line bg-card p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[18px] font-bold text-ink">{item.nombre || "(sin nombre)"}</span>
        <span className="font-mono text-[13px] text-ink-2">{item.gtin ?? "sin código"}</span>
        <span className="ml-auto">
          <EstadoRenglon item={item} a={a} />
        </span>
      </div>
      <p className="-mt-3 text-[12.5px] text-ink-2">
        {item.principio_activo ? `Principio activo: ${item.principio_activo}` : item.en_maestro ? "Del catálogo nacional" : "No está en el catálogo nacional (solo trae medicamentos)."}
        {item.ya_en_botica && <span className="font-semibold text-warn"> · Ya está cargado en esta botica</span>}
      </p>

      <label className="flex flex-col gap-1">
        {etiqueta("nombre", "Nombre en el sistema")}
        <input
          aria-label="Nombre del producto"
          data-campo="nombre"
          value={item.nombre}
          maxLength={200}
          autoComplete="off"
          onChange={(e) => acc.onCambio({ nombre: e.target.value })}
          className={cn(campoCss, "w-full text-[17px] font-semibold", falta("nombre") && campoMal)}
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-3">
        {campo("factor", "Unidades por caja", { modo: "numeric", placeholder: "und", nota: "Cuéntalas en la caja" })}
        <div className="flex flex-col gap-1">
          {etiqueta("suelto", "¿Se vende suelto? (por unidad)")}
          <SiNo valor={item.suelto} disabled={caja === 1 && item.suelto !== true} marcado={falta("suelto")} etiqueta="¿Se vende suelto?" onCambio={(v) => alCambiarSiNo(item, acc, "suelto", v)} />
        </div>
        <div className="flex flex-col gap-1">
          {etiqueta(falta("por_blister") ? "por_blister" : "blister", "¿Viene en blísteres?")}
          <Blisteres item={item} marcado={falta("blister")} marcadoPb={falta("por_blister")} onCambio={acc.onCambio} onSiNo={(v) => alCambiarSiNo(item, acc, "blister", v)} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {campo("precio_unidad", "Precio de cada unidad (S/)", { disabled: item.suelto !== true, placeholder: "0.00" })}
        {campo("precio_blister", "Precio del blíster (S/)", { disabled: item.blister !== true, placeholder: "0.00" })}
        {campo("precio_caja", porUnidad ? "Precio de la caja (S/, opcional)" : "Precio de la caja (S/)", { placeholder: porUnidad ? "opcional" : "0.00" })}
      </div>

      <div className="flex flex-col gap-1 sm:max-w-[calc((100%-2rem)/3)]">
        {etiqueta("requiere_receta", "¿Pide receta?")}
        <SiNo alerta valor={item.requiere_receta} marcado={falta("requiere_receta")} etiqueta="¿Pide receta?" onCambio={(v) => acc.onCambio({ requiere_receta: v })} />
      </div>

      {/* Los lotes: uno por vencimiento, cada uno con lo que se contó de él. */}
      <section role="group" aria-label={`Lotes de ${nombre}`} className="flex flex-col gap-2">
        <h3 className="text-[14px] font-bold text-ink">Lotes (uno por vencimiento)</h3>
        {item.lotes.map((l, i) => {
          const v = leerVence(l.vence);
          const venceIso = "iso" in v ? v.iso : null;
          const cajas = leerEntero(l.cajas) ?? 0;
          const campoL = (c: CampoLote, texto: string, opc: { modo?: "numeric" | "text"; disabled?: boolean; placeholder?: string } = {}) => (
            <label className="flex min-w-0 flex-col gap-1">
              {etiqueta(c, texto, l.id)}
              <input
                aria-label={etiquetaLote(c, i, nombre)}
                data-campo={c}
                data-lote={l.id}
                value={l[c]}
                disabled={opc.disabled}
                placeholder={opc.disabled ? "no aplica" : opc.placeholder}
                inputMode={opc.modo ?? "text"}
                autoComplete="off"
                onChange={(e) => acc.onCambioLote(l.id, { [c]: e.target.value })}
                className={cn(campoCss, "w-full", falta(c, l.id) && campoMal)}
              />
            </label>
          );
          return (
            <div key={l.id} className="grid grid-cols-2 items-end gap-3 rounded-[10px] border border-line-inset bg-inset p-3 lg:grid-cols-[minmax(120px,1.3fr)_minmax(110px,1fr)_auto_minmax(80px,0.7fr)_minmax(80px,0.7fr)_auto]">
              {campoL("lote", item.lotes.length > 1 ? `Lote ${i + 1}: número` : "Número de lote")}
              {campoL("vence", venceIso ? `Vence (${mesAnio(venceIso)})` : "Vence (mes/año)", { placeholder: "03/2027" })}
              <div className="flex flex-col gap-1">
                {etiqueta("cajas", "Cajas cerradas", l.id)}
                <span className="flex items-center gap-1">
                  <button type="button" onClick={() => acc.onCambioLote(l.id, { cajas: String(Math.max(0, cajas - 1)) })} className="h-12 w-11 flex-none rounded-[9px] border border-line-input bg-card text-[18px] font-bold" aria-label={i === 0 ? "Una caja menos" : `Una caja menos del lote ${i + 1}`}>
                    −
                  </button>
                  <input
                    aria-label={etiquetaLote("cajas", i, nombre)}
                    data-campo="cajas"
                    data-lote={l.id}
                    value={l.cajas}
                    inputMode="numeric"
                    autoComplete="off"
                    onChange={(e) => acc.onCambioLote(l.id, { cajas: e.target.value })}
                    className={cn(campoCss, "w-[64px] text-center", falta("cajas", l.id) && campoMal)}
                  />
                  <button type="button" onClick={() => acc.onCambioLote(l.id, { cajas: String(cajas + 1) })} className="h-12 w-11 flex-none rounded-[9px] border border-line-input bg-card text-[18px] font-bold" aria-label={i === 0 ? "Una caja más" : `Una caja más del lote ${i + 1}`}>
                    +
                  </button>
                </span>
              </div>
              {campoL("blisteres", "Blísteres", { modo: "numeric", disabled: item.blister !== true })}
              {campoL("sueltas", "Sueltas", { modo: "numeric", disabled: item.suelto !== true })}
              <span className="col-span-2 flex items-center justify-between gap-2 pb-3 lg:col-span-1 lg:justify-start">
                <span className="text-[13px] font-semibold tabular-nums text-ink-2">= {unidadesLote(item, l)} und</span>
                {item.lotes.length > 1 && (
                  <button type="button" onClick={() => acc.onQuitarLote(l.id)} aria-label={`Quitar el lote ${i + 1} de ${nombre}`} className="h-9 w-9 rounded-[8px] text-[14px] text-ink-3 hover:bg-hover-btn hover:text-accent-ink">
                    ✕
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </section>

      <p className="text-[17px] font-bold text-ink">
        {total} {total === 1 ? "unidad" : "unidades"} en total
      </p>

      {a && a.problemas.length > 0 && (
        <div className="rounded-[10px] border border-accent-soft-2 bg-accent-soft px-4 py-3 text-[14px] text-accent-ink">
          <p className="font-semibold">Falta o está mal:</p>
          <ul className="mt-1 list-disc pl-5">
            {a.problemas.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {a && a.avisos.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-[13px] text-warn">
          {a.avisos.map((t) => (
            <li key={t}>Ojo: {t}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={acc.onAgregarLote} className="min-h-[44px]">
          + Otro lote de este producto
        </Button>
        <Button variant="outline" onClick={acc.onQuitar} className="min-h-[44px] text-accent-ink">
          Quitar este producto
        </Button>
      </div>
    </div>
  );
}

// ---------------- Lo capturado: la lista corta, con filtros (como captura.html) ----------------
function vencePrimero(item: Item): string | null {
  const isos = item.lotes.map((l) => leerVence(l.vence)).flatMap((v) => ("iso" in v ? [v.iso] : []));
  return isos.length ? mesAnio(isos.sort()[0]!) : null;
}
function precioCorto(item: Item): string {
  const p = item.precio_caja.trim() || item.precio_blister.trim() || item.precio_unidad.trim();
  return p ? `S/ ${p}` : "—";
}

function LoCapturado({ items, analisis, abiertoId, onAbrir }: { items: Item[]; analisis: Map<string, AnalisisItem>; abiertoId: string | null; onAbrir: (id: string) => void }) {
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [buscar, setBuscar] = useState("");
  const listo = (i: Item) => !!i.carga_id || (analisis.get(i.id)?.problemas.length ?? 1) === 0;
  const q = buscar.trim().toLowerCase();
  const vistos = items.filter((i) => (filtro === "todos" ? true : filtro === "listos" ? listo(i) : !listo(i))).filter((i) => !q || i.nombre.toLowerCase().includes(q) || (i.gtin ?? "").includes(q));
  const pill = (f: Filtro, texto: string) => (
    <button
      type="button"
      aria-pressed={filtro === f}
      onClick={() => setFiltro(f)}
      className={cn("min-h-[36px] rounded-full border px-3.5 text-[13px] font-semibold", filtro === f ? "border-ink-surface-2 bg-ink-surface-2 text-on-dark" : "border-line-input bg-card text-ink-emph hover:bg-hover-btn")}
    >
      {texto}
    </button>
  );
  return (
    <section aria-label="Lo capturado" className="rounded-[12px] border border-line bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="mr-1 text-[15px] font-bold text-ink">Lo capturado</h3>
        {pill("todos", "Todos")}
        {pill("problemas", "Con problemas")}
        {pill("listos", "Listos")}
        <Input aria-label="Buscar en lo capturado" placeholder="Buscar por nombre o código" value={buscar} onChange={(e) => setBuscar(e.target.value)} className="min-h-[40px] min-w-0 flex-1 basis-[180px] text-[14px] sm:ml-auto sm:max-w-[240px]" />
      </div>
      {items.length === 0 ? (
        <p className="mt-3 px-1 text-[13.5px] text-ink-2">Todavía no capturaste nada.</p>
      ) : vistos.length === 0 ? (
        <p className="mt-3 px-1 text-[13.5px] text-ink-2">Nada con ese filtro.</p>
      ) : (
        <table className="mt-3 w-full border-collapse text-[13.5px]">
          <thead>
            <tr className="border-b border-line-row-head text-left text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
              <th className="py-2 pr-2 font-bold">Estado</th>
              <th className="py-2 pr-2 font-bold max-sm:hidden">Código</th>
              <th className="py-2 pr-2 font-bold">Nombre</th>
              <th className="py-2 pr-2 text-right font-bold">Unidades</th>
              <th className="py-2 pr-2 text-right font-bold max-md:hidden">Precio</th>
              <th className="py-2 font-bold max-md:hidden">Vence primero</th>
            </tr>
          </thead>
          <tbody>
            {vistos.map((i) => (
              <tr
                key={i.id}
                id={`fila-${i.id}`}
                onClick={() => onAbrir(i.id)}
                className={cn("cursor-pointer border-b border-line-row hover:bg-hover-btn", abiertoId === i.id && "bg-inset-2", i.carga_id && "opacity-70")}
              >
                <td className="py-2 pr-2">
                  <EstadoRenglon item={i} a={analisis.get(i.id) ?? null} corto />
                </td>
                <td className="py-2 pr-2 font-mono text-[12.5px] text-ink-2 max-sm:hidden">{i.gtin ?? "—"}</td>
                <td className="py-2 pr-2">
                  <button type="button" onClick={() => onAbrir(i.id)} className="text-left font-semibold text-ink hover:underline">
                    {i.nombre || "(sin nombre)"}
                  </button>
                </td>
                <td className="py-2 pr-2 text-right tabular-nums">{unidadesItem(i)}</td>
                <td className="py-2 pr-2 text-right tabular-nums max-md:hidden">{precioCorto(i)}</td>
                <td className="py-2 tabular-nums max-md:hidden">{vencePrimero(i) ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---------------- Subir repisa: vista previa en tres grupos ----------------
type Previa = { dry_run: true; listos: number; fuera: { id: string; nombre: string; problemas: string[] }[]; ya_en_botica: { id: string; nombre: string }[]; resumen: { nuevos: number; ya_en_catalogo: number } | null };
type Resultado = { creados: number; agregados: number; omitidos: { nombre: string }[]; fallidos: { nombre: string; error: string }[]; rechazadas: { nombre: string; motivos: string[] }[]; fuera: unknown[]; notas: { nombre: string; nota: string }[] };

function SubirRepisa({ repisaId, sucursalId, pendientes, onCerrar }: { repisaId: string; sucursalId?: string | undefined; pendientes: number; onCerrar: () => void }) {
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  useEffect(() => {
    // Lo último tecleado tiene medio segundo para guardarse antes de pedir la vista previa.
    const t = window.setTimeout(() => {
      void mutar<Previa>(conSuc(`/ingreso/repisas/${repisaId}/subir?dry_run=1`, sucursalId), { method: "POST" })
        .then(setPrevia)
        .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    }, pendientes ? 900 : 0);
    return () => window.clearTimeout(t);
  }, [repisaId, sucursalId, pendientes]);

  async function confirmar() {
    setSubiendo(true);
    try {
      setResultado(await mutar<Resultado>(conSuc(`/ingreso/repisas/${repisaId}/subir`, sucursalId), { method: "POST" }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubiendo(false);
    }
  }

  const nuevos = previa ? previa.listos - previa.ya_en_botica.length : 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-surface/40 p-3" onClick={onCerrar}>
      <div role="dialog" aria-modal="true" aria-label="Subir repisa" onClick={(e) => e.stopPropagation()} className="flex max-h-[calc(100dvh-24px)] w-full max-w-xl flex-col overflow-y-auto rounded-[14px] border border-line bg-card p-5">
        {resultado ? (
          <>
            <h2 className="text-[20px] font-bold text-ink">Repisa subida</h2>
            <p className="mt-2 text-[14px] text-ink">
              Entraron <strong>{resultado.creados + resultado.agregados}</strong> productos al inventario
              {resultado.omitidos.length > 0 && `; ${resultado.omitidos.length} ya estaban y no se tocaron`}.
            </p>
            {[...resultado.fallidos.map((f) => `${f.nombre}: ${f.error}`), ...resultado.rechazadas.map((r) => `${r.nombre}: ${r.motivos.join(", ")}`)].map((t) => (
              <p key={t} className="mt-1 text-[13px] text-accent-ink">
                • {t}
              </p>
            ))}
            {/* Lo que entró con un aviso (el blíster que la cadena no tiene: lo agrega el Dueño). */}
            {(resultado.notas ?? []).map((n) => (
              <p key={`${n.nombre}-${n.nota}`} className="mt-1 text-[13px] text-warn">
                • {n.nombre}: {n.nota}
              </p>
            ))}
            <p className="mt-2 text-[12.5px] text-ink-2">Si algo salió mal, el Encargado puede deshacer esta carga desde Catálogo › Importar.</p>
            <Button onClick={onCerrar} className="mt-4 min-h-[48px] justify-center">
              Seguir
            </Button>
          </>
        ) : (
          <>
            <h2 className="text-[20px] font-bold text-ink">Subir repisa</h2>
            {error && <p className="mt-2 text-[13px] text-accent-ink">{error}</p>}
            {!previa && !error && <p className="mt-3 text-[13px] text-ink-3">Revisando la repisa…</p>}
            {previa && (
              <div className="mt-3 flex flex-col gap-3">
                {previa.fuera.length > 0 && (
                  <details open className="rounded-[10px] border border-accent bg-accent-soft px-3 py-2">
                    <summary className="cursor-pointer text-[14px] font-bold text-accent-ink">Con algo por completar ({previa.fuera.length}) — se quedan en la repisa</summary>
                    <ul className="mt-1 flex flex-col gap-1 text-[13px] text-ink">
                      {previa.fuera.map((f) => (
                        <li key={f.id}>
                          <strong>{f.nombre}</strong>: {f.problemas.join(" · ")}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                <details open className="rounded-[10px] border border-ok bg-ok-soft px-3 py-2">
                  <summary className="cursor-pointer text-[14px] font-bold text-ok">Entra al inventario ({nuevos})</summary>
                  <p className="mt-1 text-[12.5px] text-ink-2">Con sus precios, sus lotes con su vencimiento y la repisa donde está.</p>
                </details>
                {previa.ya_en_botica.length > 0 && (
                  <details className="rounded-[10px] border border-line px-3 py-2">
                    <summary className="cursor-pointer text-[14px] font-bold text-ink-emph">Ya estaba en la botica ({previa.ya_en_botica.length}) — no se suma</summary>
                    <ul className="mt-1 text-[13px] text-ink">
                      {previa.ya_en_botica.map((y) => (
                        <li key={y.id}>{y.nombre}</li>
                      ))}
                    </ul>
                    <p className="mt-1 text-[12.5px] text-ink-2">Para sumar unidades a algo que ya estaba, usa Recepción.</p>
                  </details>
                )}
                <div className="mt-1 flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={onCerrar} className="min-h-[48px]">
                    Seguir cargando
                  </Button>
                  <Button onClick={() => void confirmar()} disabled={subiendo || previa.listos === 0} className="min-h-[48px] bg-ok hover:bg-ok hover:opacity-90">
                    {subiendo ? "Subiendo…" : `Subir ${previa.listos} ${previa.listos === 1 ? "producto" : "productos"}`}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
